import { FC, useState } from 'react';
import { Modal, Typography } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import { shopItemCategories } from 'types/MarketplaceItems';
import { APP } from 'config';
import { formatCountry } from 'utils/format';
import { Stack } from '@mui/material';
import {
  FilterButton,
  Filters,
  SelectFilter,
  ShopItemsFilter,
  SuppliersFilter,
} from 'components/filters';
import { isEqual } from 'lodash';
import {
  benefitCategories,
  BenefitShopItemsConfig,
  useRemoveShopItemsConfig,
  useUpdateShopItemsConfig,
} from 'store/benefits';
import CurrencyRangeFilter from 'components/filters/CurrencyRangeFilter';
import { taxExemptionStatuses, useShopItemsByBenefit } from 'store/shop';
import { useMe } from 'state/Administrators';

type Values = NonNullable<BenefitShopItemsConfig>;

const INITIAL_VALUES: Values = {
  benefitCategories: [],
  items: [],
  price: null,
  categories: [],
  countries: [],
  cities: [],
  suppliers: {
    value: [],
    isInverted: false,
  },
  taxExemptionStatus: [],
  taxExemptionCountries: [],
};

interface Props extends ModalProps {
  benefitId: string;
  config: BenefitShopItemsConfig;
}

export const BenefitShopItemsConfigModal: FC<Props> = ({
  closeModal,
  benefitId,
  config: savedConfig,
}) => {
  const { t, i18n } = useTranslation();
  const { taxExemptionCountries } = useMe();
  const predefinedToasts = usePredefinedToasts();
  const hasConfig = savedConfig !== null;
  const config = savedConfig ?? INITIAL_VALUES;

  const [filters, setFilters] = useState<Values>({
    ...config,
    taxExemptionCountries,
  });

  const { data: shopItems, isLoading } = useShopItemsByBenefit(
    benefitId,
    filters,
  );

  const { mutate: updateConfig, isLoading: isUpdating } =
    useUpdateShopItemsConfig();
  const { mutate: removeConfig, isLoading: isRemoving } =
    useRemoveShopItemsConfig();

  const hasFiltersChanged = !isEqual(config, filters);
  const canSaveConfig = !hasConfig || hasFiltersChanged;

  return (
    <Modal.Content
      title={t('recognitions.rewards.actions.set_config')}
      actions={[
        ...(hasConfig
          ? [
              {
                variant: 'danger-outline',
                label: t('recognitions.rewards.actions.remove_config'),
                loading: isRemoving,
                onClick: () => {
                  removeConfig(
                    { benefitId },
                    {
                      onSuccess: () => {
                        closeModal();
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
              } as const,
            ]
          : []),
        {
          variant: 'neutral-outline',
          label: t('common.cancel'),
          onClick: () => closeModal(),
        } as const,
        ...(canSaveConfig
          ? [
              {
                variant: 'primary',
                label: t('marketplace.confirmSettings', {
                  count: shopItems?.total,
                }),
                loading: isLoading || isUpdating,
                onClick: () => {
                  updateConfig(
                    { benefitId, filters },
                    {
                      onSuccess: () => {
                        closeModal();
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
              } as const,
            ]
          : []),
      ]}
    >
      <Stack gap={2}>
        <Typography variant="p1" color="textSecondary">
          {t('marketplace.autoAssignmentDescription')}
        </Typography>

        <Filters
          value={filters}
          initialValues={INITIAL_VALUES}
          onChange={setFilters}
        >
          {({ value: values, initialValues, setFilter }) => (
            <>
              <FilterButton
                label={t('marketplaceItems.supplier')}
                value={values.suppliers}
                initialValue={initialValues.suppliers}
                onChange={(v) => setFilter('suppliers', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SuppliersFilter
                    value={value}
                    onApplyFilter={applyFilter}
                    onClearFilter={clearFilter}
                    onCancel={close}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('benefits.country')}
                value={values.countries}
                initialValue={initialValues.countries}
                onChange={(v) => setFilter('countries', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    multiple
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                    options={APP.countries.alpha3.map((alpha3) => ({
                      key: alpha3,
                      label: formatCountry(i18n.language, alpha3),
                    }))}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('common.city.label')}
                value={values.cities}
                initialValue={initialValues.cities}
                onChange={(v) => setFilter('cities', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    multiple
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                    options={APP.cities.map((city) => ({
                      key: city,
                      label: city,
                    }))}
                  />
                )}
              </FilterButton>

              {taxExemptionCountries.map((country) => (
                <FilterButton
                  label={t('taxExemptions.country.label', {
                    country: formatCountry(i18n.language, country),
                  })}
                  value={values.taxExemptionStatus}
                  initialValue={initialValues.taxExemptionStatus}
                  onChange={(v) => setFilter('taxExemptionStatus', v)}
                  key={country}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SelectFilter
                      name="taxExemptionStatus"
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      multiple
                      searchable={false}
                      options={taxExemptionStatuses.map((status) => ({
                        key: status,
                        label: t(`taxExemptions.status.${status}`),
                      }))}
                    />
                  )}
                </FilterButton>
              ))}

              <FilterButton
                label={t('marketplace.filtersBenefitCategory')}
                value={values.benefitCategories}
                initialValue={initialValues.benefitCategories}
                onChange={(v) => setFilter('benefitCategories', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    multiple
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                    options={benefitCategories.map((category) => ({
                      key: category,
                      label: t(`menu.${category}`),
                    }))}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('marketplaceOrders.itemName')}
                value={values.items}
                initialValue={initialValues.items}
                onChange={(v) => setFilter('items', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <ShopItemsFilter
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('marketplace.filtersItemCategory')}
                value={values.categories}
                initialValue={initialValues.categories}
                onChange={(v) => setFilter('categories', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    multiple
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                    options={shopItemCategories.map((category) => ({
                      key: category,
                      label: t(`marketplaceItems.itemCategory-${category}`),
                    }))}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('marketplace.filtersPriceRange')}
                value={values.price}
                initialValue={initialValues.price}
                onChange={(v) => setFilter('price', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <CurrencyRangeFilter
                    value={value}
                    onApplyFilter={applyFilter}
                    onClearFilter={clearFilter}
                    onCancel={close}
                  />
                )}
              </FilterButton>
            </>
          )}
        </Filters>
      </Stack>
    </Modal.Content>
  );
};
